import { anmeldekopf, basisUrl, rufe } from './client';

/**
 * Gutscheine von Kostentraegern (PRD-38, PRF-17, CRM-157, CRM-163): Erfassen
 * an Buchung oder Auftrag, Sammelabrechnung je Kostentraeger ohne erneute
 * Umsatzsteuer (E-70). Die Art des Kostentraegers ist eine Werteliste (E-74),
 * je Kostentraeger gibt es eine Liste fester Gutscheinwerte (CRM-163).
 */

export type BgGutscheinstatus = 'erfasst' | 'offen' | 'abgerechnet' | 'bezahlt' | 'verworfen';
export type BgAbrechnungsstatus = 'abgerechnet' | 'bezahlt' | 'storniert';

export const GUTSCHEINSTATUSNAME: Readonly<Record<BgGutscheinstatus, string>> = {
  erfasst: 'erfasst',
  offen: 'angerechnet, offen',
  abgerechnet: 'abgerechnet',
  bezahlt: 'bezahlt',
  verworfen: 'verworfen',
};

export const ABRECHNUNGSSTATUSNAME: Readonly<Record<BgAbrechnungsstatus, string>> = {
  abgerechnet: 'abgerechnet',
  bezahlt: 'bezahlt',
  storniert: 'storniert',
};

export type Kostentraeger = {
  readonly id: string;
  readonly kundennummer: string;
  readonly name: string;
  /** E-74 (CRM-163): Schluessel der Art aus der Werteliste. */
  readonly art: string;
  readonly artBezeichnung: string;
};

/** E-74 (CRM-163): ein Eintrag der Werteliste „Art des Kostenträgers". */
export type Kostentraegerart = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly reihenfolge: number;
  /** false: außer Gebrauch — wird nicht mehr angeboten, bleibt lesbar. */
  readonly aktiv: boolean;
};

export type KostentraegerartEingabe = {
  readonly bezeichnung: string;
  readonly reihenfolge: number;
  readonly aktiv: boolean;
};

/** PRD-38 (CRM-163): ein fester Gutscheinwert aus der Liste eines Kostenträgers. */
export type Gutscheinwert = {
  readonly id: string;
  readonly kostentraeger: string;
  readonly bezeichnung: string;
  /** Betrag mit Punkt, etwa "120.00". */
  readonly betrag: string;
  readonly gueltigAb: string | null;
  readonly gueltigBis: string | null;
  /** false: veraltet, wird bei der Erfassung nicht mehr angeboten. */
  readonly aktiv: boolean;
  /** Aktiv und heute im Gültigkeitszeitraum. */
  readonly gueltig: boolean;
  /** Wie viele Gutscheine ihn tragen; ein benutzter Wert wird nicht gelöscht. */
  readonly benutzt: number;
};

export type GutscheinwertEingabe = {
  readonly bezeichnung: string;
  /** "120.00" oder "120,00". */
  readonly betrag: string;
  readonly gueltigAb: string | null;
  readonly gueltigBis: string | null;
  readonly aktiv: boolean;
};

export type BgGutschein = {
  readonly id: string;
  readonly kostentraeger: string;
  readonly kostentraegerName: string;
  readonly nummer: string;
  readonly name: string;
  readonly betrag: string;
  /** Der gewählte Listeneintrag (CRM-163); null bei eigenem Betrag. */
  readonly gutscheinwert: string | null;
  readonly gutscheinwertBezeichnung: string | null;
  readonly buchung: string | null;
  readonly teilnehmer: string | null;
  readonly teilnehmerName: string | null;
  readonly auftragsposition: string | null;
  readonly rechnung: string | null;
  readonly rechnungsnummer: string | null;
  readonly leistungstag: string | null;
  readonly leistung: string | null;
  readonly status: BgGutscheinstatus;
  readonly abrechnung: string | null;
  readonly abrechnungsnummer: string | null;
  /** AeA-71: die Vormerkung, gegen die der Gutschein zählt. */
  readonly vormerkung: string | null;
  readonly angelegtAm: string;
};

/**
 * AeA-71 (CRM-191): angekündigte Gutscheine eines Kostenträgers als Anzahl,
 * ohne Namen und Nummer, an einer Angebots- oder Auftragsposition. Keine
 * Anrechnung (PRF-17) — die Rechnung rechnet nur benannte Gutscheine an.
 */
export type Gutscheinvormerkung = {
  readonly id: string;
  readonly angebotsposition: string | null;
  readonly auftragsposition: string | null;
  readonly position: string;
  readonly kostentraeger: string;
  readonly kostentraegerName: string;
  readonly gutscheinwert: string | null;
  readonly gutscheinwertBezeichnung: string | null;
  readonly betrag: string;
  readonly anzahl: number;
  /** Nicht verworfene Gutscheine mit Nummer und Name, die dagegen zählen. */
  readonly eingetragen: number;
  readonly offen: number;
  readonly bemerkung: string | null;
  readonly aufgeloestAm: string | null;
  readonly angelegtAm: string;
};

export type Vormerken = {
  readonly angebotsposition: string | null;
  readonly auftragsposition: string | null;
  readonly kostentraeger: string;
  /** Wie beim Erfassen: ein Wert aus der Liste ODER ein eigener Betrag. */
  readonly gutscheinwert: string | null;
  readonly betrag: string | null;
  readonly anzahl: number;
  readonly bemerkung: string | null;
};

export type Abrechnungszeile = {
  readonly gutschein: string;
  readonly nummer: string;
  readonly name: string;
  readonly teilnehmer: string | null;
  readonly leistung: string | null;
  readonly leistungstag: string | null;
  readonly rechnungsnummer: string | null;
  readonly betrag: string;
};

export type BgAbrechnungskopf = {
  readonly id: string;
  readonly belegnummer: string;
  readonly kostentraeger: string;
  readonly kostentraegerName: string;
  /** Bezeichnung der Art (CRM-163); null, wenn der Kunde keine Art mehr trägt. */
  readonly kostentraegerArt: string | null;
  readonly belegdatum: string;
  readonly zeitraumVon: string | null;
  readonly zeitraumBis: string | null;
  readonly summe: string;
  readonly anzahl: number;
  readonly status: BgAbrechnungsstatus;
  readonly bezahltAm: string | null;
  readonly stornogrund: string | null;
};

export type BgAbrechnung = BgAbrechnungskopf & { readonly zeilen: readonly Abrechnungszeile[] };

export type GutscheinErfassen = {
  readonly kostentraeger: string;
  readonly nummer: string;
  /** Leer: der Name des Teilnehmers. */
  readonly name: string | null;
  /**
   * PRD-38 (CRM-163): ein Wert aus der Liste ODER ein eigener Betrag. Mit Wert
   * nimmt der Server dessen Betrag; `betrag` bleibt dann null.
   */
  readonly gutscheinwert: string | null;
  readonly betrag: string | null;
  readonly teilnehmer: string | null;
  readonly auftragsposition: string | null;
  /** AeA-71: gegen welche Vormerkung; null wählt der Server, wenn genau eine passt. */
  readonly vormerkung: string | null;
};

export async function kostentraeger(): Promise<readonly Kostentraeger[]> {
  return rufe('/bg-zuschuss/kostentraeger');
}

/**
 * E-74 (CRM-162, CRM-163): Die Art des Kostenträgers setzt, wer Kunden anlegen
 * oder ändern darf; null nimmt das Kennzeichen weg.
 */
export async function kostentraegerSetzen(kunde: string, art: string | null): Promise<void> {
  await rufe(`/bg-zuschuss/kostentraeger/${kunde}`, { verfahren: 'PUT', rumpf: { art } });
}

/** E-74 (CRM-163): die Werteliste der Arten, auch die außer Gebrauch. */
export async function arten(): Promise<readonly Kostentraegerart[]> {
  return rufe('/bg-zuschuss/kostentraegerarten');
}

export async function artAnlegen(
  eingabe: KostentraegerartEingabe & { readonly schluessel: string },
): Promise<void> {
  await rufe('/bg-zuschuss/kostentraegerarten', { verfahren: 'POST', rumpf: eingabe });
}

/** Der Schlüssel bleibt; gelöscht wird nicht, `aktiv: false` nimmt die Art außer Gebrauch. */
export async function artAendern(schluessel: string, eingabe: KostentraegerartEingabe): Promise<void> {
  await rufe(`/bg-zuschuss/kostentraegerarten/${encodeURIComponent(schluessel)}`, {
    verfahren: 'PUT',
    rumpf: eingabe,
  });
}

/** PRD-38 (CRM-163): die Gutscheinwerte eines Kostenträgers, auch veraltete. */
export async function werte(kunde: string): Promise<readonly Gutscheinwert[]> {
  return rufe(`/bg-zuschuss/kostentraeger/${kunde}/werte`);
}

export async function wertAnlegen(kunde: string, eingabe: GutscheinwertEingabe): Promise<Gutscheinwert> {
  return rufe(`/bg-zuschuss/kostentraeger/${kunde}/werte`, { verfahren: 'POST', rumpf: eingabe });
}

export async function wertAendern(id: string, eingabe: GutscheinwertEingabe): Promise<Gutscheinwert> {
  return rufe(`/bg-zuschuss/werte/${id}`, { verfahren: 'PUT', rumpf: eingabe });
}

/** Nur ein nie benutzter Wert; einen benutzten weist der Server ab — er wird als veraltet markiert. */
export async function wertLoeschen(id: string): Promise<void> {
  await rufe(`/bg-zuschuss/werte/${id}`, { verfahren: 'DELETE' });
}

export async function gutscheine(filter: {
  readonly buchung?: string;
  readonly auftrag?: string;
  readonly kostentraeger?: string;
  readonly status?: BgGutscheinstatus;
}): Promise<readonly BgGutschein[]> {
  const frage = new URLSearchParams(
    Object.entries(filter).filter((e): e is [string, string] => e[1] !== undefined),
  ).toString();
  return rufe(`/bg-zuschuss/gutscheine${frage === '' ? '' : `?${frage}`}`);
}

/** PRD-38: Ein abweichender Name kommt als Hinweis zurück, nicht als Fehler. */
export async function erfassen(
  eingabe: GutscheinErfassen,
): Promise<{ readonly gutschein: BgGutschein; readonly hinweis: string | null }> {
  return rufe('/bg-zuschuss/gutscheine', { verfahren: 'POST', rumpf: eingabe });
}

/** AeA-71: die Vormerkungen eines Angebots oder Auftrags (nach Position). */
export async function vormerkungen(
  bezug: { readonly angebot: string } | { readonly auftrag: string },
): Promise<readonly Gutscheinvormerkung[]> {
  const frage = 'angebot' in bezug ? `angebot=${bezug.angebot}` : `auftrag=${bezug.auftrag}`;
  return rufe(`/bg-zuschuss/vormerkungen?${frage}`);
}

/** AeA-71: die offenen Vormerkungen, gegen die ein Gutschein an diesem Ziel zählen kann. */
export async function passendeVormerkungen(
  ziel: { readonly teilnehmer: string } | { readonly auftragsposition: string },
): Promise<readonly Gutscheinvormerkung[]> {
  const frage = 'teilnehmer' in ziel ? `teilnehmer=${ziel.teilnehmer}` : `auftragsposition=${ziel.auftragsposition}`;
  return rufe(`/bg-zuschuss/vormerkungen?${frage}`);
}

export async function vormerken(eingabe: Vormerken): Promise<Gutscheinvormerkung> {
  return rufe('/bg-zuschuss/vormerkungen', { verfahren: 'POST', rumpf: eingabe });
}

/** Kostenträger und Betrag bleiben; nie unter das schon Eingetragene. */
export async function vormerkungAendern(
  id: string,
  eingabe: { readonly anzahl: number; readonly bemerkung: string | null },
): Promise<Gutscheinvormerkung> {
  return rufe(`/bg-zuschuss/vormerkungen/${id}`, { verfahren: 'PUT', rumpf: eingabe });
}

/** LOG-01: aufgelöst statt gelöscht; nur, solange nichts eingetragen ist. */
export async function vormerkungAufloesen(id: string, grund: string | null): Promise<Gutscheinvormerkung> {
  return rufe(`/bg-zuschuss/vormerkungen/${id}/aufloesen`, { verfahren: 'POST', rumpf: { grund } });
}

export async function verwerfen(id: string, grund: string): Promise<void> {
  await rufe(`/bg-zuschuss/gutscheine/${id}/verwerfen`, { verfahren: 'POST', rumpf: { grund } });
}

function zeitraumFrage(von: string | null, bis: string | null): string {
  return `${von === null ? '' : `&von=${von}`}${bis === null ? '' : `&bis=${bis}`}`;
}

export async function offene(
  kostentraegerId: string,
  von: string | null,
  bis: string | null,
): Promise<readonly Abrechnungszeile[]> {
  return rufe(`/bg-zuschuss/offene?kostentraeger=${kostentraegerId}${zeitraumFrage(von, bis)}`);
}

export async function abrechnen(eingabe: {
  readonly kostentraeger: string;
  readonly von: string | null;
  readonly bis: string | null;
}): Promise<BgAbrechnung> {
  return rufe('/bg-zuschuss/abrechnungen', { verfahren: 'POST', rumpf: eingabe });
}

export async function abrechnungen(kostentraegerId?: string): Promise<readonly BgAbrechnungskopf[]> {
  return rufe(`/bg-zuschuss/abrechnungen${kostentraegerId === undefined ? '' : `?kostentraeger=${kostentraegerId}`}`);
}

export async function abrechnung(id: string): Promise<BgAbrechnung> {
  return rufe(`/bg-zuschuss/abrechnungen/${id}`);
}

export async function bezahlt(id: string, am: string): Promise<BgAbrechnung> {
  return rufe(`/bg-zuschuss/abrechnungen/${id}/bezahlt`, { verfahren: 'POST', rumpf: { am } });
}

export async function stornieren(id: string, grund: string): Promise<BgAbrechnung> {
  return rufe(`/bg-zuschuss/abrechnungen/${id}/stornieren`, { verfahren: 'POST', rumpf: { grund } });
}

/** PDF oder CSV der Abrechnung — nicht über `rufe`, die Antwort ist kein JSON. */
export async function abrechnungsdatei(
  id: string,
  art: 'pdf' | 'csv',
): Promise<{ readonly blob: Blob; readonly dateiname: string }> {
  const antwort = await fetch(`${basisUrl()}/bg-zuschuss/abrechnungen/${id}/${art}`, { headers: anmeldekopf() });
  if (!antwort.ok) throw new Error(`Die Abrechnung konnte nicht als ${art.toUpperCase()} geladen werden.`);
  const kopf = antwort.headers.get('content-disposition') ?? '';
  return { blob: await antwort.blob(), dateiname: /filename="([^"]+)"/.exec(kopf)?.[1] ?? `abrechnung.${art}` };
}
